import React from "react";
import { Popover, DatePicker, Select } from "antd";
import { CloseOutlined, CalendarOutlined, UserOutlined } from "@ant-design/icons";
import styled from "styled-components";
import moment from "moment";
import { getFileIcon, getFileName } from "./fileUtils";

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;

  .selected-file-card {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    background: #fafafa;
    max-width: 300px;

    .file-thumb {
      width: 32px;
      height: 32px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: 2px;
      background: #f0f0f0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .file-type-icon {
        font-size: 18px;
        color: #999;
      }
    }

    .file-info {
      flex: 1;
      min-width: 0;
      font-size: 12px;
      color: #333;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .file-actions {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;

      .action-icon {
        cursor: pointer;
        font-size: 14px;
        padding: 2px;
        border-radius: 2px;
        transition: color 0.2s;
      }

      .action-icon-active {
        color: #0b2a1b;
      }

      .action-icon-inactive {
        color: #d9d9d9;
      }
    }

    .remove-btn {
      flex-shrink: 0;
      cursor: pointer;
      color: #999;
      font-size: 12px;
      padding: 2px;
      border-radius: 2px;
      transition: all 0.2s;

      &:hover {
        color: #ff4d4f;
        background: #fff1f0;
      }
    }
  }
`;

const SelectedFilesDisplay = ({
  files = [],
  onRemove,
  onExpiryDateChange,
  onUserChange,
  users = [],
  dateFormat = "DD/MM/YYYY",
}) => {
  if (files.length === 0) return null;

  return (
    <Wrapper>
      {files.map((file) => {
        const hasExpiry = !!file.expiry_date;
        const assignedUser = file.user;
        const hasUser = !!assignedUser;
        const userName = hasUser
          ? `${assignedUser.name || ""} ${assignedUser.surname || ""}`.trim()
          : "";

        return (
          <div key={file.id} className="selected-file-card">
            <div className="file-thumb">
              {file.type === "image" && file.full_path ? (
                <img src={file.full_path} alt={getFileName(file)} />
              ) : (
                getFileIcon(file.type)
              )}
            </div>
            <div className="file-info" title={getFileName(file)}>
              {getFileName(file)}
            </div>
            <div className="file-actions">
              {onUserChange && (
                <Popover
                  trigger="click"
                  placement="bottom"
                  content={
                    <div style={{ width: 220 }}>
                      <div style={{ marginBottom: 4, fontSize: 12, color: "#888" }}>
                        Assigned user
                      </div>
                      <Select
                        size="small"
                        allowClear
                        placeholder="Select user"
                        value={file.user_id || undefined}
                        onChange={(value) => onUserChange(file.id, value || null)}
                        style={{ width: "100%" }}
                      >
                        {users.map((u) => (
                          <Select.Option key={u.id} value={u.id}>
                            {`${u.name || ""} ${u.surname || ""}`.trim()}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>
                  }
                >
                  <UserOutlined
                    title={hasUser ? `User: ${userName}` : "Assign user"}
                    className={`action-icon ${hasUser ? "action-icon-active" : "action-icon-inactive"}`}
                  />
                </Popover>
              )}
              {onExpiryDateChange && (
                <Popover
                  trigger="click"
                  placement="bottom"
                  content={
                    <div style={{ width: 220 }}>
                      <div style={{ marginBottom: 4, fontSize: 12, color: "#888" }}>
                        Expiry date
                      </div>
                      <DatePicker
                        size="small"
                        format={dateFormat}
                        placeholder="Select date"
                        value={hasExpiry ? moment(file.expiry_date) : null}
                        onChange={(date) => onExpiryDateChange(file.id, date)}
                        style={{ width: "100%" }}
                      />
                    </div>
                  }
                >
                  <CalendarOutlined
                    title={
                      hasExpiry
                        ? `Expires: ${moment(file.expiry_date).format(dateFormat)}`
                        : "Set expiry date"
                    }
                    className={`action-icon ${hasExpiry ? "action-icon-active" : "action-icon-inactive"}`}
                  />
                </Popover>
              )}
            </div>
            {onRemove && (
              <CloseOutlined
                className="remove-btn"
                onClick={() => onRemove(file.id)}
              />
            )}
          </div>
        );
      })}
    </Wrapper>
  );
};

export default SelectedFilesDisplay;
